// frontend/src/pages/AdminTools.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";

import { getRelevantQuestions } from "../lib/riskQuestionSet";
import { appendAuditSnapshot, rebuildAuditSnapshotIndexFromPayloads } from "../lib/auditHistory";

import {
  isCurrentUserSuperAdmin,
  readEntitlementOverride,
  writeEntitlementOverride,
  clearEntitlementOverride,
} from "../lib/superAdmin";

const STORAGE_PREFIX = "aifine_";

const STORAGE_KEYS = {
  USER: "aifine_user",
  ACTIVE_BUSINESS_ID: "aifine_active_business_id_v1",
  BUSINESS_INDEX: "aifine_business_index_v1",
  BUSINESS_PAYLOAD_PREFIX: "aifine_business_payload_",
  CONFIG: "aifine_config",
  ANSWERS_PREFIX: "aifine_answers_business_",
  PROGRESS_PREFIX: "aifine_assessment_progress_business_",
};

const STANDARD_SINGLE_ID = "single_default";

function safeParse(raw, fallback = null) {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function clamp(n, lo, hi) {
  const v = Number(n);
  if (!Number.isFinite(v)) return lo;
  return Math.max(lo, Math.min(hi, v));
}

function getActiveBusinessId() {
  try {
    return localStorage.getItem(STORAGE_KEYS.ACTIVE_BUSINESS_ID) || "";
  } catch {
    return "";
  }
}

function loadBusinessPayload(businessId) {
  try {
    return safeParse(localStorage.getItem(`${STORAGE_KEYS.BUSINESS_PAYLOAD_PREFIX}${businessId}`), null);
  } catch {
    return null;
  }
}

function loadConfigForBusiness(businessId) {
  const payload = businessId ? loadBusinessPayload(businessId) : null;
  const fallback = safeParse(localStorage.getItem(STORAGE_KEYS.CONFIG), null);
  return payload || fallback || null;
}

function setConfigShim(payload) {
  try {
    localStorage.setItem(STORAGE_KEYS.CONFIG, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

function countAnsweredValues(obj) {
  if (!obj || typeof obj !== "object") return 0;
  let n = 0;
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v === true || v === false || v === "unsure" || v === "YES" || v === "NO" || v === "UNSURE") n += 1;
  }
  return n;
}

function randPick(weights) {
  const total = weights.reduce((acc, x) => acc + Math.max(0, Number(x.w) || 0), 0);
  if (total <= 0) return weights[0]?.v;

  let r = Math.random() * total;
  for (const item of weights) {
    r -= Math.max(0, Number(item.w) || 0);
    if (r <= 0) return item.v;
  }
  return weights[weights.length - 1]?.v;
}

function writeLocalUserPatch(patch = {}) {
  try {
    const prev = safeParse(localStorage.getItem(STORAGE_KEYS.USER), {}) || {};
    const next = {
      ...prev,
      ...patch,
      updated_at_iso: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(next));
  } catch {
    // ignore
  }
}

/**
 * IMPORTANT:
 * Dashboard behaves differently in single vs top tier:
 * - single tier Dashboard uses STANDARD_SINGLE_ID, ignoring ACTIVE_BUSINESS_ID
 * - top tier Dashboard uses ACTIVE_BUSINESS_ID
 *
 * To make Dev Tools match what Dashboard will show, we compute the "dashboard scope business id".
 */
function readTierForScope() {
  // Prefer entitlement override (AdminTools controlled)
  const ov = readEntitlementOverride();
  if (ov?.tier) return String(ov.tier).toLowerCase() === "top" ? "top" : "single";

  // Fall back to local user tier (Dashboard also writes this sometimes)
  const user = safeParse(localStorage.getItem(STORAGE_KEYS.USER), null);
  const t = user?.subscription_tier ?? user?.subscriptionTier ?? user?.tier ?? null;
  return String(t || "single").toLowerCase() === "top" ? "top" : "single";
}

function getDashboardScopeBusinessId() {
  const tier = readTierForScope();
  if (tier === "top") return getActiveBusinessId() || "";
  return STANDARD_SINGLE_ID;
}

export default function AdminTools() {
  const navigate = useNavigate();

  const [authz, setAuthz] = useState({ loading: true, allowed: false });
  const [status, setStatus] = useState(null);

  const [yesPct, setYesPct] = useState(60);
  const [noPct, setNoPct] = useState(25);
  const [unsurePct, setUnsurePct] = useState(15);

  const [seedRuns, setSeedRuns] = useState(6);
  const [seedMin, setSeedMin] = useState(45);
  const [seedMax, setSeedMax] = useState(92);

  // NOTE: businessId here is the ID Dev Tools will write to (matches Dashboard scope)
  const businessId = useMemo(() => {
    const bid = getDashboardScopeBusinessId();
    return bid || STANDARD_SINGLE_ID;
  }, [status]);

  const cfg = useMemo(() => loadConfigForBusiness(businessId), [businessId]);

  const questionCount = useMemo(() => {
    try {
      const qs = getRelevantQuestions(cfg);
      return Array.isArray(qs) ? qs.length : 0;
    } catch {
      return 0;
    }
  }, [cfg]);

  const override = useMemo(() => readEntitlementOverride(), [status]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const ok = await isCurrentUserSuperAdmin();
      if (!mounted) return;
      setAuthz({ loading: false, allowed: Boolean(ok) });
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const setMessage = (msg) => {
    setStatus({ msg, t: Date.now() });
  };

  const clearLocalTestData = () => {
    const ok = window.confirm(
      "Clear ALL local test data?\n\nThis will remove localStorage keys starting with 'aifine_' (including local business configs, in-progress answers, and saved report snapshots). It will NOT delete anything in Supabase.\n\nContinue?"
    );
    if (!ok) return;

    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const k = localStorage.key(i);
        if (!k) continue;
        if (String(k).startsWith(STORAGE_PREFIX)) keys.push(k);
      }
      keys.forEach((k) => localStorage.removeItem(k));

      setMessage(`Cleared ${keys.length} local keys.`);
    } catch {
      setMessage("Clearing local keys failed (storage error).");
    }
  };

  const autopopulateAnswers = (mode = "all") => {
    // Ensure config exists for AuditAssessment to run
    const effectiveCfg = cfg || safeParse(localStorage.getItem(STORAGE_KEYS.CONFIG), null) || null;

    if (!effectiveCfg) {
      setMessage("No active config found (run Start Audit first).");
      return;
    }

    // Guarantee aifine_config is present (AuditAssessment reads it as fallback)
    setConfigShim(effectiveCfg);

    let qs = [];
    try {
      qs = getRelevantQuestions(effectiveCfg);
    } catch {
      qs = [];
    }

    if (!Array.isArray(qs) || qs.length === 0) {
      setMessage("No questions found for current config.");
      return;
    }

    const y = clamp(yesPct, 0, 100);
    const n = clamp(noPct, 0, 100);
    const u = clamp(unsurePct, 0, 100);

    const weights = [
      { v: true, w: y },
      { v: false, w: n },
      { v: "unsure", w: u },
    ];

    const answersKey = `${STORAGE_KEYS.ANSWERS_PREFIX}${businessId}`;
    const progressKey = `${STORAGE_KEYS.PROGRESS_PREFIX}${businessId}`;

    const existing = safeParse(localStorage.getItem(answersKey), {});
    const next = existing && typeof existing === "object" ? { ...existing } : {};

    const total = qs.length;
    const fillCount = mode === "partial" ? Math.max(1, Math.floor(total * 0.35)) : total;

    for (let i = 0; i < fillCount; i += 1) {
      const q = qs[i];
      const id = q?.id;
      if (!id) continue;
      next[id] = randPick(weights);
    }

    try {
      localStorage.setItem(answersKey, JSON.stringify(next));
    } catch {
      setMessage("Failed writing answers (storage error).");
      return;
    }

    try {
      const completedCount = countAnsweredValues(next);
      const currentIndex = mode === "partial" ? Math.max(0, fillCount - 1) : 0;

      localStorage.setItem(
        progressKey,
        JSON.stringify({
          version: 1,
          business_id: businessId,
          currentIndex,
          totalQuestions: total,
          completedCount,
          updated_at_iso: new Date().toISOString(),
        })
      );
    } catch {
      // ignore
    }

    setMessage(
      mode === "partial"
        ? `Autofilled ~35% of answers for ${total} questions. Open /assessment to confirm it resumes.`
        : `Autofilled answers for ${total} questions. Open /assessment to confirm.`
    );
  };

  const applyTierOverride = (tier) => {
    const clean = String(tier || "").toLowerCase() === "top" ? "top" : "single";

    const next = {
      version: 1,
      tier: clean,
      isPaid: true, // local convenience only; PaywallGuard still enforces paid routes
      rawPlanKey: clean === "top" ? "override_top" : "override_single",
      allowReports: true,
      allowRemediation: true,
      allowConsultantWorkflow: clean === "top",
      maxBusinesses: clean === "top" ? 25 : 1,
      updated_at_iso: new Date().toISOString(),
    };

    const ok = writeEntitlementOverride(next);
    if (!ok) {
      setMessage("Failed writing override (storage error).");
      return;
    }

    // Back-compat: some pages still read local user tier for display
    writeLocalUserPatch({
      subscription_tier: clean,
      raw_tier: next.rawPlanKey,
    });

    setMessage(`Applied local entitlement override: ${clean.toUpperCase()}. Reloading…`);
    window.setTimeout(() => window.location.reload(), 650);
  };

  const clearTierOverride = () => {
    clearEntitlementOverride();
    setMessage("Cleared local entitlement override. Reloading…");
    window.setTimeout(() => window.location.reload(), 450);
  };

  const seedTrend = () => {
    const effectiveCfg = cfg || safeParse(localStorage.getItem(STORAGE_KEYS.CONFIG), null) || null;

    if (!effectiveCfg) {
      setMessage("No active config found (run Start Audit first).");
      return;
    }

    // Guarantee aifine_config is present for any downstream pages
    setConfigShim(effectiveCfg);

    const runs = clamp(seedRuns, 2, 25);
    const lo = clamp(seedMin, 0, 100);
    const hi = clamp(seedMax, 0, 100);
    const min = Math.min(lo, hi);
    const max = Math.max(lo, hi);

    const entityName = String(effectiveCfg?.clientName || "Demo Business");
    const jurs = Array.isArray(effectiveCfg?.jurisdictions) ? effectiveCfg.jurisdictions : [];

    const now = Date.now();
    const day = 24 * 60 * 60 * 1000;

    try {
      for (let i = 0; i < runs; i += 1) {
        const t = now - (runs - 1 - i) * day;

        const score = Math.round(min + Math.random() * (max - min));
        const totalQuestions = Math.max(1, questionCount || 60);
        const yesCount = Math.round((score / 100) * totalQuestions);
        const failedCount = Math.max(0, totalQuestions - yesCount);
        const unsureCount = Math.round(failedCount * 0.2);

        appendAuditSnapshot({
          snapshot_id: `${t}`,
          business_id: businessId,
          entity_name: entityName,
          generated_at: new Date(t).toISOString(),
          summary: { jurisdictions: jurs },
          assessment: {
            compliance_score_pct: score,
            total_questions: totalQuestions,
            failed_count: failedCount,
            unsure_count: unsureCount,
            failed_question_ids: [],
            unsure_question_ids: [],
            active_question_ids: [],
          },
        });
      }

      // Force-rebuild the index (defensive) so Dashboard immediately sees it
      try {
        rebuildAuditSnapshotIndexFromPayloads();
      } catch {
        // ignore
      }

      setMessage(`Seeded ${runs} snapshot(s) for Dashboard trend. Go to Dashboard and refresh once.`);
    } catch (err) {
      setMessage(`Seed Trend failed: ${String(err?.message || err || "unknown error")}`);
    }
  };

  if (authz.loading) {
    return (
      <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-auto">
          <div className="mx-auto max-w-4xl px-8 py-10">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">Checking admin access…</div>
          </div>
        </main>
      </div>
    );
  }

  if (!authz.allowed) {
    return (
      <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-auto">
          <div className="mx-auto max-w-4xl px-8 py-10">
            <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6">
              <div className="text-sm font-black text-rose-900">Not authorized</div>
              <div className="mt-2 text-sm text-rose-900/80">This page is only available to Super Admin.</div>
              <div className="mt-5">
                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  Back to Dashboard →
                </button>
              </div>
              <div className="mt-6 text-xs text-rose-900/70">
                To enable Super Admin: set <span className="font-mono">REACT_APP_SUPER_ADMIN_EMAILS</span> in{" "}
                <span className="font-mono">frontend/.env</span> and rebuild/deploy.
              </div>
            </div>

            <div className="mt-10 pb-8 text-xs text-slate-400">© 2026 Advanced Advisory Services, LLC. All Rights Reserved.</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden">
      <Sidebar />

      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-5xl px-8 py-10">
          <div className="flex items-start justify-between gap-6">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Super Admin Tools</h1>
              <p className="mt-1 text-sm text-slate-600">Local development utilities (safe to use; affects localStorage only).</p>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                  Dashboard Scope Business ID: {businessId}
                </span>
                <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                  Questions in current config: {questionCount || "—"}
                </span>
                {override?.tier ? (
                  <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-black text-amber-900">
                    Override: {String(override.tier).toUpperCase()}
                  </span>
                ) : (
                  <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                    Override: none
                  </span>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Back to Dashboard →
            </button>
          </div>

          {status?.msg ? (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">{status.msg}</div>
          ) : null}

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Clear data */}
            <div className="lg:col-span-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="text-xs font-black uppercase tracking-[0.35em] text-slate-400">Local reset</div>
              <div className="mt-2 text-xl font-extrabold tracking-tight text-slate-900">Clear local test data</div>
              <div className="mt-1 text-sm text-slate-600">
                Removes <span className="font-mono">aifine_*</span> keys from localStorage.
              </div>

              <div className="mt-5">
                <button
                  type="button"
                  onClick={clearLocalTestData}
                  className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700"
                >
                  Clear ALL local test data
                </button>
              </div>
            </div>

            {/* Tier override */}
            <div className="lg:col-span-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="text-xs font-black uppercase tracking-[0.35em] text-slate-400">Entitlement</div>
              <div className="mt-2 text-xl font-extrabold tracking-tight text-slate-900">Switch subscriber type</div>
              <div className="mt-1 text-sm text-slate-600">Sets a LOCAL override for UI gating and multi-business testing.</div>

              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => applyTierOverride("single")}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Set Single Tier
                </button>
                <button
                  type="button"
                  onClick={() => applyTierOverride("top")}
                  className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  Set Top Tier
                </button>
                <button
                  type="button"
                  onClick={clearTierOverride}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Clear Override
                </button>
              </div>
            </div>

            {/* Autofill answers */}
            <div className="lg:col-span-7 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="text-xs font-black uppercase tracking-[0.35em] text-slate-400">Assessment</div>
              <div className="mt-2 text-xl font-extrabold tracking-tight text-slate-900">Auto-populate assessment answers</div>
              <div className="mt-1 text-sm text-slate-600">Writes randomized answers into localStorage for the Dashboard-scope business.</div>

              <div className="mt-5 grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400">Yes %</div>
                  <input
                    type="number"
                    value={yesPct}
                    onChange={(e) => setYesPct(clamp(e.target.value, 0, 100))}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900"
                  />
                </div>
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400">No %</div>
                  <input
                    type="number"
                    value={noPct}
                    onChange={(e) => setNoPct(clamp(e.target.value, 0, 100))}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900"
                  />
                </div>
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400">Unsure %</div>
                  <input
                    type="number"
                    value={unsurePct}
                    onChange={(e) => setUnsurePct(clamp(e.target.value, 0, 100))}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900"
                  />
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => autopopulateAnswers("all")}
                  className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  Fill ALL questions
                </button>
                <button
                  type="button"
                  onClick={() => autopopulateAnswers("partial")}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Fill ~35% (in-progress)
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/assessment")}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Open Assessment →
                </button>
              </div>
            </div>

            {/* Seed trend */}
            <div className="lg:col-span-5 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="text-xs font-black uppercase tracking-[0.35em] text-slate-400">Dashboard</div>
              <div className="mt-2 text-xl font-extrabold tracking-tight text-slate-900">Seed trend chart</div>
              <div className="mt-1 text-sm text-slate-600">Creates local audit snapshots so the trend chart populates.</div>

              <div className="mt-5 grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400">Runs</div>
                  <input
                    type="number"
                    value={seedRuns}
                    onChange={(e) => setSeedRuns(clamp(e.target.value, 2, 25))}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900"
                  />
                </div>
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400">Min %</div>
                  <input
                    type="number"
                    value={seedMin}
                    onChange={(e) => setSeedMin(clamp(e.target.value, 0, 100))}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900"
                  />
                </div>
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400">Max %</div>
                  <input
                    type="number"
                    value={seedMax}
                    onChange={(e) => setSeedMax(clamp(e.target.value, 0, 100))}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900"
                  />
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={seedTrend}
                  className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  Seed Trend
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  View Dashboard →
                </button>
              </div>
            </div>
          </div>

          <div className="mt-10 pb-8 text-xs text-slate-400">© 2026 Advanced Advisory Services, LLC. All Rights Reserved.</div>
        </div>
      </main>
    </div>
  );
}